import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import { Row, Column } from '../Layout/Grid';
import Text from '../Text';
import { Button } from '../Button';
import MultiSelect from 'components/MultiSelect';

import IconSearch from 'img/search-icon.svg';

import {
  addKeyPressListener,
  removeKeyPressListener,
} from 'utils/searchWithEnterPress';
import { DateOutputStr } from 'utils/DateOutput';
import { Capitalize } from 'utils/String';
import { calcDataFrom, calcDataTo } from 'utils/Date';

import DomainSettingService from 'services/domainSetting.service';
import PipefyServices from 'services/pipefy.service';
import BusService from 'services/bus.service';

import { IDomainSetting } from 'models/DomainSetting';
import { BUSelect, BUType } from 'models/BusinessUnits';
import { ITableSearch } from './interface';

import './style.scss';
import PeriodDateToInput from 'components/PeriodDateToInput';

const TableSearch: React.FC<ITableSearch> = (props) => {
  const {
    inputFilter,
    fastFilters,
    statusFilters,
    setTableFilter,
    dateKey,
    tableOrderBy,
    setTableOrderBy,
    className,
    showSituationFilter,
    setCurrentPage,
    dateMin,
    dateMax,
    showGroupSelect,
    ShowStatusSelect,
    clearBtn,
    statusLabel,
    typeStatusProtocol,
    showBUs,
    setOnLoadPageTable,
    setUpdateSearch,
    updateSearch
  } = props;

  const randomTableSearchId = `${Math.random() * 1000}`;
  const [searchStr, setSearchStr] = useState('');
  const [period, setPeriod] = useState({
    from: dateMin ? DateOutputStr(dateMin.toISOString(), 'yyyy-MM-dd') : '',
    to: dateMax ? DateOutputStr(dateMax.toISOString(), 'yyyy-MM-dd') : '',
  });
  const [selectedOption, setSelectedOption] = useState(JSON.stringify({}));
  const [situationSelectedOption, setSituationSelectedOption] = useState('');

  const [phases, setPhases] = useState<any[]>([]);
  const [selectedPhase, setSelectedPhase] = useState('');

  const searchStrRef = useRef(searchStr);
  const periodRef = useRef(period);
  const selectedOptionRef = useRef(selectedOption);
  const situationSelectedOptionRef = useRef(situationSelectedOption);

  const _domainSettingService = new DomainSettingService();
  const _busService = new BusService();
  const [userProfileTypes, setUserProfileTypes] = useState(Array<IDomainSetting>());
  const [trackingTypes, setTrackingProfileTypes] = useState(Array<IDomainSetting>());
  const [profilesFilter, setProfilesFilter] = useState<any>([]);
  const [busFilter, setBUsFilter] = useState<any>([]);
  const [allBUs, setAllBUs] = useState<Array<BUSelect>>([]);
  const user = useSelector((state: RootState) => state.user);
  const [showResponsibleGroupDropdown, setShowResponsibleGroupDropdown] = useState<boolean>(false);
  const [showBUsDropdown, setShowBUsDropdown] = useState<boolean>(false);
  const [newData, setNewData] = useState('');
  const customer = useSelector((state: RootState) => state.customer);

  const _pipefyService = new PipefyServices();

  useEffect(() => {
    setPeriod({
      from: dateMin ? DateOutputStr(dateMin.toISOString(), 'yyyy-MM-dd') : '',
      to: dateMax ? DateOutputStr(dateMax.toISOString(), 'yyyy-MM-dd') : '',
    });
  }, [customer, user?.selectedCustomerId]);

  function makeFilter() {
    const searchStrCurrent = searchStrRef.current;
    const periodCurrent = periodRef.current;
    const selectedOptionCurrent = selectedOptionRef.current;
    const situationSelectedOptionCurrent = situationSelectedOptionRef.current;
    /**
     * First format
     */
    const model = {
      searchStrCurrent,
      period: {
        start: periodCurrent.from ? `${periodCurrent.from}T00:00:00.000Z` : '',
        end: periodCurrent.to ? `${periodCurrent.to}T23:59:59.999Z` : '',
      },
      selectedOption: JSON.parse(selectedOptionCurrent),
    };

    const filterData: any = {
      and: {},
    };

    if (model.searchStrCurrent) {
      const query = inputFilter?.filters(model.searchStrCurrent.toLowerCase());
      if (query.length > 0) filterData.and = { ...filterData.and, or: query };
    } else {
      inputFilter?.filters(model.searchStrCurrent.toLowerCase());
    }

    /**
     * Date OData format
     */
    if (dateKey) {
      if (periodCurrent.from && periodCurrent.to) {
        filterData.and = {
          ...filterData.and,
          and: [
            { [dateKey]: { ge: new Date(model.period.start) } },
            { [dateKey]: { le: new Date(model.period.end) } },
          ],
        };
      } else if (periodCurrent.from && !periodCurrent.to) {
        filterData.and = {
          ...filterData.and,
          [dateKey]: { ge: new Date(model.period.start) },
        };
      } else if (!periodCurrent.from && periodCurrent.to) {
        filterData.and = {
          ...filterData.and,
          [dateKey]: { le: new Date(model.period.end) },
        };
      }

      /**
       * Select option format
       */
      if (Object.entries(model.selectedOption).length) {
        filterData.and = { ...filterData.and, ...model.selectedOption };
      }

      if (situationSelectedOptionCurrent) {
        filterData.and = {
          ...filterData.and,
          status: { eq: situationSelectedOptionCurrent },
        };
      }
    }

    if (showGroupSelect) {
      if (
        profilesFilter && profilesFilter.length > 0
      ) {
        const itensFilter: any = [];
        profilesFilter.map((filter: any) => {
          itensFilter.push({
            responsibleGroup: { eq: filter }
          });
        });

        filterData.and.and.push({
          or: itensFilter
        });
      }

      if (selectedPhase) {
        filterData.and = {
          ...filterData.and,
          phaseStatus: { eq: selectedPhase },
        };
      }

      if (showBUs) {
        if (busFilter.length > 0) {
          const busFilterMenu: any = [];
          busFilter.map((filter: any) => {
            busFilterMenu.push({
              businessUnits: { eq: filter }
            });
          });

          // Adiciona todas os protocolos com BU null
          busFilterMenu.push({
            businessUnits: { eq: null }
          });

          filterData.and.and.push({
            or: busFilterMenu
          });
        }
      }
    }

    setCurrentPage(0);
    setUpdateSearch && setUpdateSearch(!updateSearch);
    setOnLoadPageTable && setOnLoadPageTable(true);
    setTableFilter((filters: any) => ({ ...filters, ...filterData }));
  }

  function makeFilterLoadPage(filterGroup: any, filterBU: any) {
    const periodCurrent = periodRef.current;
    /**
     * First format
     */
    const model = {
      period: {
        start: periodCurrent.from ? `${periodCurrent.from}T00:00:00.000Z` : '',
        end: periodCurrent.to ? `${periodCurrent.to}T23:59:59.999Z` : '',
      },
    };

    const filterData: any = {
      and: {}
    };

    /**
     * Date OData format
     */
    if (dateKey) {
      if (periodCurrent.from && periodCurrent.to) {
        filterData.and = {
          ...filterData.and,
          and: [
            { [dateKey]: { ge: new Date(model.period.start) } },
            { [dateKey]: { le: new Date(model.period.end) } },
          ],
        };
      } else if (periodCurrent.from && !periodCurrent.to) {
        filterData.and = {
          ...filterData.and,
          [dateKey]: { ge: new Date(model.period.start) },
        };
      } else if (!periodCurrent.from && periodCurrent.to) {
        filterData.and = {
          ...filterData.and,
          [dateKey]: { le: new Date(model.period.end) },
        };
      }
    }

    if (showGroupSelect) {
      const itensGroupFilter: any = [];
      filterGroup.map((filter: any) => {
        itensGroupFilter.push({
          responsibleGroup: { eq: filter }
        });
      });

      if (filterGroup) {
        filterData.and.and.push({
          or: itensGroupFilter
        });
      }

      if (showBUs) {
        if (filterBU.length > 0) {
          const busFilterData: any = [];
          filterBU.map((filter: any) => {
            busFilterData.push({
              businessUnits: { eq: filter.id }
            });
          });

          // Adiciona todas os protocolos com BU null
          busFilterData.push({
            businessUnits: { eq: null }
          });

          if (filterGroup) {
            filterData.and.and.push({
              or: busFilterData
            });
          }
        }
      }
    }

    setCurrentPage(0);
    setOnLoadPageTable && setOnLoadPageTable(true);
    setTableFilter((filters: any) => ({ ...filters, ...filterData }));
  }

  useEffect(() => {
    if (user?.profileType) {
      if (!(user?.profileType?.length === 1 && user.profileType[0] === 'CUSTOMER')) {
        makeFilterLoadPage(user?.profileType, user?.businessUnits);
      }
    }

    const buSelected: any = [];
    user?.businessUnits?.map(bu => {
      buSelected.push(bu.id);
    });
    setBUsFilter(buSelected);

    const userProfileLogged: any = user?.profileType;

    const indexCustomerLogged = userProfileLogged?.indexOf("CUSTOMER");

    if (indexCustomerLogged !== -1) {
      userProfileLogged?.splice(indexCustomerLogged, 1);
    }

    setProfilesFilter(user?.profileType);
  }, [user]);

  /**
   * Set table order with value of clicked fastFilter
   * @param value filter value
   */
  function setFastFilter(value: any) {
    if (tableOrderBy === value) return;

    setTableOrderBy(value);
  }

  /**
   * Only returns a filter style
   * @param filter
   */
  const fastSearchStyles = (filter: any) =>
    `${tableOrderBy === filter.value && 'border-bottom-2'} font-bold ${tableOrderBy === filter.value ? 'color-primary' : 'color-dark-gray'
    }`;

  useEffect(() => {
    searchStrRef.current = searchStr;
  }, [searchStr]);

  useEffect(() => {
    periodRef.current = period;
  }, [period]);

  useEffect(() => {
    selectedOptionRef.current = selectedOption;
  }, [selectedOption]);

  useEffect(() => {
    situationSelectedOptionRef.current = situationSelectedOption;
  }, [situationSelectedOption]);

  useEffect(() => {
    addKeyPressListener(randomTableSearchId, makeFilter);

    return () => {
      removeKeyPressListener(randomTableSearchId, makeFilter);
    };
  }, []);

  useEffect(() => {
    setSearchStr('');
    setSituationSelectedOption('');
  }, [user?.selectedCustomerId]);

  useEffect(() => {
    _busService
      .get(undefined, undefined, 'name asc', {})
      .then((result: any) => {
        if (result && result.data) {
          const bus: Array<BUType> = result.data;
          let allBusSelect: Array<BUSelect> = [];
          bus.forEach((bu: BUType) => {
            const item = {
              value: `${bu.identifier} - ${bu.name}`,
              key: bu.id,
            };

            allBusSelect.push({ ...allBUs, ...item });
          });
          setAllBUs(allBusSelect);
        }
      });
  }, []);

  useEffect(() => {
    _domainSettingService
      .get(undefined, undefined, 'value asc', {
        and: [
          { identifier: { eq: 'UserProfileTypes' } },
          { enabled: { eq: true } },
        ],
      })
      .then((result: any) => {
        if (result && result.data) {
          const _userProfileTypes: Array<IDomainSetting> = result.data;

          const indexCustomer = _userProfileTypes.findIndex(item => item.key === "CUSTOMER");

          if (indexCustomer !== -1) {
            _userProfileTypes.splice(indexCustomer, 1);
          }

          setUserProfileTypes(_userProfileTypes);
        }
      });
  }, []);

  useEffect(() => {
    _domainSettingService
      .get(undefined, undefined, 'value asc', {
        and: [
          { identifier: { eq: 'TrackingTypes' } },
          { enabled: { eq: true } },
        ],
      })
      .then((result: any) => {
        if (result && result.data) {
          const _trackingTypes: Array<IDomainSetting> = result.data;

          setTrackingProfileTypes(_trackingTypes);
        }
      });
  }, []);

  useEffect(() => {
    async function getPhases() {
      const response = await _pipefyService.getPhases();

      let result;

      if (typeStatusProtocol === 'Abertos') {
        result = response.filter(p => p.done === false);
      } else {
        result = response.filter(p => p.done === true);
      }

      setPhases(result);
    }

    getPhases();
  }, []);

  const clearFilter = () => {
    const filterData: any = {
      and: {},
    };

    setSearchStr('');
    setSelectedPhase('');
    setProfilesFilter([]);
    setBUsFilter([]);
    setCurrentPage(0);
    setTableFilter((filters: any) => ({ ...filters, ...filterData }));
  };

  return (
    <Row
      id={randomTableSearchId}
      tabIndex={0}
      className={`mt-4 align-center flex-md-column align-md-none flex-wrap justify-md-none bg-white ${className || 'px-3 py-3'}`}
      style={{ borderRadius: '10px 10px 0px 0px' }}
    >

      <Column className="mr-2 mr-md-2 w-md-100 ajustment-filter">
        <Text className="mb-1 color-blue-gray" size={12} weight="bold">
          Filtro
        </Text>

        <Row>
          <input
            onChange={(e) => setSearchStr(e.target.value)}
            type="text"
            placeholder={inputFilter?.placeholder || ''}
            value={searchStr}
            className="w-sm-100 w-md-100"
          />
        </Row>

      </Column>
      {dateKey && (

        <Column className="flex-1 mr-2 mr-md-2 w-md-100 mt-md-3">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
            Período
          </Text>
          <Row className="flex-1 align-center">
            <input
              className="flex-1"
              value={period.from}
              onChange={(e) => {
                setPeriod({ ...period, from: e.target.value });
                setNewData('end');
              }}
              type="date"
              max={period.to}
              placeholder="Início"
              onBlur={() => calcDataTo(period, newData, setPeriod)}
              id="date_first_order"
            />
            <Text className="mx-1 color-blue-gray" size={12} weight="bold">
              a
            </Text>
            <PeriodDateToInput
              id="date_end_order"
              setNewData={setNewData}
              onBlur={() => calcDataFrom(period, newData, setPeriod)}
              className="flex-1"
              period={period}
              setPeriod={setPeriod}
              placeHolder={"Fim"}
            />
          </Row>
        </Column>
      )}

      {statusFilters && (
        <Column className="mr-2 mr-lg-0 mt-md-3 flex-1 w-md-100 ajustment-status">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
            Status
          </Text>
          <Row className="flex-1">
            <select
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              className="flex-1"
            >
              <option value={JSON.stringify({})}>Todos</option>
              {statusFilters.map((filter, index) => (
                <option
                  key={index}
                  label={filter.label}
                  value={JSON.stringify(filter.value)}
                />
              ))}
            </select>
          </Row>
        </Column>
      )}
      {showSituationFilter && (
        <Column className="mr-2 mr-lg-0 mt-md-3 flex-1 w-md-100 ajustment-situation">
          <Text className="mr-2 mb-1 color-blue-gray" size={12} weight="bold">
            Situação
          </Text>
          <Row className="flex-1">
            <select
              value={situationSelectedOption}
              onChange={(e) => setSituationSelectedOption(e.target.value)}
              className="flex-1"
            >
              <option value="">Todos</option>
              {trackingTypes.map((filter, index) => (
                <option
                  key={index}
                  label={Capitalize(filter.value)}
                  value={filter.value}
                />
              ))}
            </select>
          </Row>
        </Column>
      )}
      {showGroupSelect && (

        <Column className="mr-2 mr-md-0">
          <Text size={12} className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2">
            Grupo Responsável
          </Text>
          <Row className="align-center flex-sm-column">
            <MultiSelect
              itensSelected={profilesFilter}
              allItems={userProfileTypes}
              width={"250px"}
              className="w-sm-100 w-md-100"
              placeHolderOne="grupo"
              placeHolderMany="grupos"
              style={{ minWidth: 140 }}
              show={showResponsibleGroupDropdown}
              setShow={setShowResponsibleGroupDropdown}
            />
          </Row>
        </Column>
      )}
      {showBUs && (

        <Column className="mr-2 mr-md-0">
          <Text size={12} className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2">
            BU's
          </Text>
          <Row className="align-center flex-sm-column">
            <MultiSelect
              itensSelected={busFilter}
              allItems={allBUs}
              width={"330px"}
              className="w-sm-100 w-md-100"
              placeHolderOne="BU"
              placeHolderMany="BUs"
              style={{ minWidth: 140 }}
              show={showBUsDropdown}
              setShow={setShowBUsDropdown}
            />
          </Row>
        </Column>
      )}
      {ShowStatusSelect && (
        <Column className="mr-2 mr-md-0">
          <Text size={12} className="mt-md-4 mb-1 color-blue-gray mb-lg-1 mr-2">
            {statusLabel}
          </Text>
          <Row className="align-center flex-sm-column w-sm-100 overflow-hidden">
            <select
              name="status"
              defaultValue={selectedPhase}
              onChange={(e) => setSelectedPhase(e.target.value)}
              value={selectedPhase}
              style={{ width: 150 }}
            >
              <option value="">Todos</option>
              {phases.map((phase, index) => {
                return <option key={index} label={phase.name} value={phase.name} />;
              })}
            </select>
          </Row>
        </Column>
      )}
      <Column className="mr-2 mt-md-3 w-md-100 pt-3 pt-md-0 ajustment-button">
        <Row>
          <Button
            id="btn_table_search"
            tabIndex={0}
            className="focus-button"
            width="auto"
            onClick={makeFilter}
          >
            <img src={IconSearch} alt="" />
          </Button>
        </Row>
      </Column>
      {fastFilters && (
        <Row className="mt-3 mt-md-3 align-center justify-between flex-1 w-md-100 flex-md-column">
          <Row className="align-center flex-1 justify-start justify-md-center w-md-100 align-md-none">
            {fastFilters.map((filter, index) => (
              <Button
                key={index}
                className={`${index > 0 && 'ml-0 ml-md-0'}`}
                onClick={() => setFastFilter(filter.value)}
                width="auto"
              >
                <Text
                  size={12}
                  className={`border-primary ${fastSearchStyles(
                    filter
                  )} pb-1 px-2 ajustment-fontsize-fast-filter`}
                >
                  {filter.label}
                </Text>
              </Button>
            ))}
          </Row>
        </Row>
      )}
      {clearBtn && (
        <Row className="justify-end justify-lg-end justify-md-center">
          <Button onClick={() => clearFilter()}>
            <Text size={14} className="color-primary mt-2 ajustment-fontsize-clear-filter">
              x Limpar filtros
            </Text>
          </Button>
        </Row>
      )}
    </Row>
  );
};

export default TableSearch;